import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GeoId } from '../../types/geo.types';
import { useRecoilState } from 'recoil';
import { Treemap, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Heading, TypographyColor, TypographyType, Body, BodyColor, BodyType } from '../../ui-elements';
import { userCurrencyState } from '../../states';


interface Props {
  categories: any[];
  geoCode: GeoId;
}

// Format value based on the currency without symbol and decimal points
const formatValue = (value: number, currency: string) => {
  const locales = {
    USD: 'en-US',
    INR: 'en-IN',
    EUR: 'de-DE', // Add more currencies as needed
  };

  const locale = locales[currency as keyof typeof locales] || 'en-US'; // Default to USD
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0, // Remove decimal points
  }).format(value);
};
// Flatten the data for Recharts
const flattenData = (categories: any[]) =>
  categories.map((category) => ({
    fill: category.color,
    children: category.subCategories.map((sub: any) => ({
      name: sub.name,
      value: sub.value,
      fill: sub.color,
      parent: sub.parent,
      parentAverage: sub.parentAverage,
      growthRateText: sub.growthRateText,
    })),
  }));

// Custom Tooltip

const CustomTooltip = ({ active, payload, geoCode }: any) => {
  // Ensure the hook is called unconditionally
  const [selCurrency] = useRecoilState(userCurrencyState);

  // Check if 'catchment' is present in the URL
  const isCatchmentInUrl = window.location.href.toLowerCase().includes('catchment');

  if (active && payload && payload?.length) {
    const { payload: data } = payload[0]; // Extract the data object from the payload

    // Show average data if: String(geoCode) !== '1' OR (String(geoCode) === '1' AND catchment is in URL)
    const shouldShowAverageData = String(geoCode) !== '1' || (String(geoCode) === '1' && isCatchmentInUrl);

    return (
      <div
        id='treemap-tooltip'
        className="treemap-tooltip"
      >
        <div className="margin-right-3" style={{ height: '0.91vw', display: 'flex', alignItems: 'center' }}>
          <div style={{ height: '10px', width: '10px', backgroundColor: data.fill }} className="rounded-circle"></div>
        </div>
        <div className="d-flex flex-column justify-content-start align-items-start">
          <Heading
            title={`${data.name} PoI: ${formatValue(data.value, selCurrency)}`}
            type={TypographyType.h5}
            colour={TypographyColor.primary}
            style={{ display: 'flex', flexWrap: 'nowrap' }}
          />
          {shouldShowAverageData && data.parent && (
            <>
              <Body
                type={BodyType.p4}
                color={BodyColor.secondary}
                classname="text-start"
              >
                {data.growthRateText}
              </Body>
              <Body
                type={BodyType.p4}
                color={BodyColor.secondary}
                classname="text-start"
              >
                {`${data.parent} Average: ${formatValue(data.parentAverage, selCurrency)} `}
              </Body>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const CustomNode = ({ depth, x, y, width, height, name, value, fill }: any) => {
  const fontSize = 12;
  const padding = 10;
  // Ensure the hook is called unconditionally
  const [selCurrency] = useRecoilState(userCurrencyState);

  if (depth === 0) return null;

  // Function to truncate text and add ellipsis if it's too long
  const truncateText = (text: string, maxWidth: number, fontSize: number) => {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) {
      return text; // If context is null, return the original text
    }

    context.font = `${fontSize}px Poppins`;

    // Check if the full text fits
    const textWidth = context.measureText(text).width;
    if (textWidth <= maxWidth) {
      return text; // Text fits, no truncation needed
    }

    // Binary search to find the optimal truncation point
    const ellipsis = '...';
    const ellipsisWidth = context.measureText(ellipsis).width;
    const availableWidthForText = maxWidth - ellipsisWidth;

    if (availableWidthForText <= 0) {
      return ''; // Not enough space even for ellipsis
    }

    let left = 0;
    let right = text.length;
    let bestFit = '';

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const testText = text.slice(0, mid);
      const testWidth = context.measureText(testText).width;

      if (testWidth <= availableWidthForText) {
        bestFit = testText;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return bestFit.length > 0 ? bestFit + ellipsis : '';
  };



  // Minimum width and height checks
  const minWidth = fontSize * 3; // Approx. 3 characters
  const minHeight = fontSize * 2 + 4; // Minimum height to fit two lines of text

  // Check if there's enough space to render any text
  const isSpaceEnough = width >= minWidth && height >= minHeight;

  // Calculate available width after padding (subtracting 2 * padding)
  const availableWidth = width - 2 * padding;

  // Truncate the name with the available width
  const truncatedName = isSpaceEnough ? truncateText(name, availableWidth, fontSize) : '';

  return (
    <g>
      {/* Rectangle for the node */}
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" />

      {/* Render the name and value only if there's enough space */}
      {isSpaceEnough && (
        <>
          {/* Render the name */}
          <text
            x={x + width / 2}
            y={y + height / 2 - fontSize / 2}
            textAnchor="middle"
            fontSize={fontSize}
            fill="#fff"
            fontWeight="lighter"
            letterSpacing={1}
          >
            {truncatedName}
          </text>

          {/* Render the value */}
          {truncatedName !== '' && (
            <text
              x={x + width / 2}
              y={y + height / 2 + fontSize / 2 + 4}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#fff"
            >
              {formatValue(value, selCurrency)}
            </text>
          )}
        </>
      )}
    </g>
  );
};

export const TreemapChart: React.FC<Props> = ({ categories, geoCode }) => {
  // Ensure the hook is called unconditionally
  const [selCurrency] = useRecoilState(userCurrencyState);
  const totalValue = categories.reduce((acc, category) => acc + category.value, 0);
  const sortedCategories = categories.sort((a, b) => b.value - a.value);
  // const data = flattenData(sortedCategories);
  // Use useMemo to ensure data is recalculated only when categories change
  const data = useMemo(() => flattenData(sortedCategories), [categories]);
  // Create an array of refs, one for each category
  const tooltipRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [tooltipPosition, setTooltipPosition] = useState<Record<number, React.CSSProperties>>({});

  const adjustTooltipPositionOnHover = (event: MouseEvent) => {
    const viewportWidth = window.innerWidth;
    const container = document.getElementById('treemap-container');
    const tooltip = document.querySelector('.recharts-tooltip-wrapper-left') as HTMLElement;
    const cursorLeft = event.clientX;
    const cursorBottom = event.clientY;
    const cursorRight = window.innerWidth - cursorLeft;
    const cursorTop = window.innerHeight - cursorBottom;

    if (tooltip && container) {
      // Wait for the tooltip to be rendered in the DOM
      requestAnimationFrame(() => {
        const tooltipRect = tooltip.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        let tooltipHeight = tooltipRect.height;
        let tooltipWidth = tooltipRect.width;

        if (tooltipWidth === 0 || tooltipHeight === 0) {
          console.warn("Tooltip dimensions are not ready.");
          tooltipWidth = 200;
          tooltipHeight = 100;
          return;
        }

        let leftAdjustment = 0;

        // Adjust the tooltip position if it goes out of the viewport on the right
        // if (containerRect.left < cursorLeft
        //   && cursorLeft + tooltipWidth + 20 > viewportWidth
        //   && containerRect.bottom > cursorBottom + tooltipHeight
        // ) {
        //   tooltip.style.left = `${cursorLeft + 10}px !important`;
        //   tooltip.style.top = `${cursorBottom + 10}px !important`;
        // }
        // else {
        //   tooltip.style.right = '0px !important';
        // }
        if (
          event.clientX + tooltipWidth + 20 > viewportWidth &&
          tooltipRect.left !== containerRect.left // Ensure tooltip is not at container's left edge
        ) {
          leftAdjustment = -tooltipWidth - 20; // Shift tooltip to the left
        }

        // Adjust the vertical position if the tooltip goes out of the container's bottom

        const containerBottom = containerRect.bottom;
        let topAdjustment = 50; // Default downward shift
        if (cursorBottom + tooltipHeight > containerBottom) {

          topAdjustment = -tooltipHeight - 20; // Shift tooltip upwards
        }

        // Apply the adjustments
        // tooltip.style.transform = `translate(${leftAdjustment}px, ${topAdjustment}px)`;
        tooltip.style.transform = `translate(${leftAdjustment}px, 200px)`;
      });
    }
  };

  useEffect(() => {
    const container = document.getElementById('treemap-container');
    if (container) {
      container.addEventListener('mousemove', adjustTooltipPositionOnHover);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', adjustTooltipPositionOnHover);
      }
    };
  }, []);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLDivElement>,
    index: number
  ) => {
    const tooltipRef = tooltipRefs.current[index];
    if (!tooltipRef) return;

    const tooltipRect = tooltipRef.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Get the header's bounding rect for reference
    const headerRect = event.currentTarget.getBoundingClientRect();

    let leftAdjustment = 0;

    // Check for overflow on the left side
    if (tooltipRect.left < 0) {
      leftAdjustment = -tooltipRect.left + 10; // Shift tooltip right
    }
    // Check for overflow on the right side
    else if (tooltipRect.right + 50 > viewportWidth) {
      leftAdjustment = -30; // Shift tooltip left      
    }

    // Update tooltip position dynamically
    setTooltipPosition((prev) => ({
      ...prev,
      [index]: {
        transform: `translateX(calc(-50% + ${leftAdjustment}px))`,
      },
    }));
  };

  // Calculate the percentage widths and check for overflow
  const percentageWidths = useMemo(() => {
    return categories.map((category) =>
      Math.round((category.value / totalValue) * 100)
    );
  }, [categories]);

  const adjustedWidths = useMemo(() => {
    let totalWidth = 100; // Total percentage width
    const roundedWidths = percentageWidths.map((width) => Math.min(width, totalWidth));
    const totalRounded = roundedWidths.reduce((acc, width) => acc + width, 0);

    // Redistribute any rounding error
    const adjustment = totalWidth - totalRounded;
    if (adjustment !== 0) {
      roundedWidths[0] += adjustment; // Adjust the first element to balance the difference
    }

    return roundedWidths;
  }, [percentageWidths]);


  return (
    <div
      key={categories.length}
      id="treemap-container"
      style={{
        display: 'flex',
        justifyContent: 'start',
        width: '100%',
        borderRadius: '10px',
        overflow: 'hidden',
        gap: '1px',
        position: 'relative',
      }}
    >
      {categories.map((category, index) => {
        // Calculate the percentage width for each category
        const percentageWidth = adjustedWidths[index];

        // Ensure refs array is large enough
        if (!tooltipRefs.current[index]) {
          tooltipRefs.current[index] = null!;
        }

        return (
          <div
            key={`category-${index}`}
            style={{
              flex: `0 0 calc(${percentageWidth}% - 1px)`, // Adjust for rounding errors
              borderRadius: '10px',
              boxSizing: 'border-box',
              flexShrink: 1,
              flexGrow: 0,
            }}
          >
            {/* Category Header */}
            <div
              className='treemap-header'
              onMouseEnter={(event) => handleMouseEnter(event, index)}
            >
              <Heading
                title={category.name}
                type={TypographyType.h5}
                colour={TypographyColor.primary}
                classname='text-truncate'
                style={{ color: category.color, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
              />
              <Heading
                title={formatValue(category.value, selCurrency)}
                type={TypographyType.h4}
                colour={TypographyColor.primary}
                classname='text-truncate'
                style={{ color: category.color }}
              />
              {/* Category Tooltip */}
              <div
                className="treemap-header-tooltip"
                ref={(el) => (tooltipRefs.current[index] = el)}
                style={{
                  ...tooltipPosition[index], // Apply dynamic positioning
                }}
              >
                <Heading
                  title={category.name}
                  type={TypographyType.h5}
                  colour={TypographyColor.primary}
                  classname='text-truncate'
                />
                <Heading
                  title={`PoI: ${formatValue(category.value, selCurrency)}`}
                  type={TypographyType.h4}
                  colour={TypographyColor.primary}
                  classname='text-truncate'
                />
              </div>
            </div>

            {/* Treemap for the Category */}
            <ResponsiveContainer width="100%" height={400}>
              <Treemap
                data={data[index]?.children}
                dataKey="value"
                nameKey="name"
                // aspectRatio={4 / 3}
                stroke="#fff"
                fill="#8884d8"
                content={<CustomNode />} // Pass custom node renderer
                isAnimationActive={false} // Disable animation
                style={{ position: 'relative' }}
              >
                <Tooltip content={<CustomTooltip geoCode={geoCode} />} />
                {data[index]?.children?.map((entry: any, subIndex: number) => (
                  <Cell key={`cell-${subIndex}`} fill={entry.fill} stroke="#fff" />
                ))}
              </Treemap>
            </ResponsiveContainer>
          </div>
        )
      }
      )}
    </div>
  );
};